/* eslint-disable no-irregular-whitespace */
import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
// import TawkToChat from "../components/TawkToChat"; // Removed if not essential
import * as Sentry from "@sentry/react"; // Keep Sentry if used elsewhere
import { analytics } from "../firebase-config"; // Keep analytics for other events if needed
import { useSelector, useDispatch } from "react-redux";
import { logEvent } from "firebase/analytics";
import { useToast } from "../components/Alerts";
import AssignJobOfferModal from "../components/AssignJobOfferModal";
import UsersTable from "../components/UsersTable";
import AssessmentsTable from "../components/AssessmentsTable";
import JobOffersTable from "../components/JobOffersTable";
import ActionButton from "../components/common/ActionButton";
import {
  fetchJobPostings,
  selectJobPostings,
} from "../utils/redux/jobPostingsSlice";
import {
  selectAllUsers,
  selectUserByEmail,
  fetchUserByEmail,
  fetchAllUsers,
} from "../utils/redux/userDataSlice";
import CreateJobModal from "../components/createJobModal";
import LicenseRequiredModal from "../components/LicenseRequiredModal";
import {
  fetchAndSetCompanyData,
  selectAllCompanys,
} from "../utils/redux/companySlice";
import {
  fetchAssessments,
  selectAssessments,
} from "../utils/redux/assessmentSlice";
import { useAuth } from "../useAuth";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { useTranslation } from "react-i18next"; // Import useTranslation
import { useCreateJob } from "../hooks/useCreateJob"; // Adjust path
import TawkToChat from "../components/TawkToChat";


const DashboardPage = () => {
  const { currentUser } = useAuth();
  const jobPostings = useSelector(selectJobPostings);
  const assessments = useSelector(selectAssessments);
  const [activeTab, setActiveTab] = useState("users");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [hasAnyLicense, setHasAnyLicense] = useState(false); // Controls Create vs LicenseRequired modal
  // State for all license counts
  const [basicLicenseCount, setBasicLicenseCount] = useState(0);
  const [growthLicenseCount, setGrowthLicenseCount] = useState(0); // Added
  const [proLicenseCount, setProLicenseCount] = useState(0); // 'pro' for Professional
  const [premiumLicenseCount, setPremiumLicenseCount] = useState(0); // Added

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [newJobData, setNewJobData] = useState({}); // Keep for modal form state
  const userData = useSelector((state) =>
    currentUser ? selectUserByEmail(state, currentUser.email) : null
  );
  const usersData = useSelector((state) =>
    userData?.tenantId ? selectAllUsers(state, userData.tenantId) : []
  );
  const companyData = useSelector(selectAllCompanys); // This is an array
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const { t, i18n } = useTranslation("DashboardPage"); // Assuming 'DashboardPage' namespace
  const { addToast } = useToast();

  // --- Use the custom hook for creating jobs ---
  const { createJob, isLoading: isCreatingJob } = useCreateJob({
    currentUser,
    userData,
    companyData,
    // eslint-disable-next-line no-unused-vars
    onSuccess: (createdJob) => {
      setIsCreateModalOpen(false);
      setNewJobData({});
      if (userData?.tenantId) {
        dispatch(fetchJobPostings({ tenantId: userData.tenantId }));
        dispatch(fetchAndSetCompanyData(userData.tenantId));
      }
      addToast({ type: "success", message: t("jobCreatedSuccess") });
    },
    onError: (error) => {
      console.error("Hook reported error:", error);
      // Toast handled by hook
    },
  });
  // --- End hook usage ---

  useEffect(() => {
    // Set language based on browser preference initially
    const browserLang = navigator.language.split("-")[0];
    if (i18n.language !== browserLang && ["en", "fr"].includes(browserLang)) {
      i18n.changeLanguage(browserLang);
    }
  }, [i18n]);

  useEffect(() => {
    document.title = t("dashboardTitle");
    return () => {
      document.title = t("defaultTitle");
    };
  }, [t]);

  // Initial data fetching effect
  useEffect(() => {
    let isMounted = true;
    setIsFetchingInitialData(true);
    const fetchData = async () => {
      if (currentUser?.email && isMounted) {
        try {
          const userAction = await dispatch(fetchUserByEmail(currentUser.email));
          if (fetchUserByEmail.fulfilled.match(userAction) && userAction.payload?.tenantId) {
            const tenantId = userAction.payload.tenantId;
            await Promise.all([
              dispatch(fetchAllUsers(tenantId)),
              dispatch(fetchAndSetCompanyData(tenantId)),
              dispatch(fetchAssessments({ tenantId })),
              dispatch(fetchJobPostings({ tenantId })),
            ]);
          } else {
            console.error("Failed to fetch user data or tenantId.");
            addToast({ type: "error", message: t("errorFetchingUserData") });
          }
        } catch (error) {
          console.error("Error fetching initial dashboard data:", error);
          if (process.env.REACT_APP_ENVIRONEMENT === "prod") { Sentry.captureException(error); }
          addToast({ type: "error", message: t("errorFetchingData") });
        } finally {
          if (isMounted) { setIsFetchingInitialData(false); }
        }
      } else if (!currentUser && isMounted) {
        setIsFetchingInitialData(false); // Not logged in
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, [currentUser, dispatch, addToast, t]);

  // Effect to update local license counts and hasAnyLicense when companyData changes
  useEffect(() => {
    if (companyData && companyData.length > 0) {
      const currentCompany = companyData[0];
      // ** Update state for all 4 license types **
      setBasicLicenseCount(currentCompany.basicLicense ?? 0);
      setGrowthLicenseCount(currentCompany.growthLicense ?? 0); // Assuming field name 'growthLicense'
      setProLicenseCount(currentCompany.proLicense ?? 0);
      setPremiumLicenseCount(currentCompany.premiumLicense ?? 0); // Assuming field name 'premiumLicense'

      // ** Update hasAnyLicense check to include all types **
      setHasAnyLicense(
        (currentCompany.basicLicense ?? 0) +
        (currentCompany.growthLicense ?? 0) + // Added Growth
        (currentCompany.proLicense ?? 0) +
        (currentCompany.premiumLicense ?? 0) + // Added Premium
        // Keep trial logic as needed
        (currentCompany.trialLicense ?? 0) > 0 || currentCompany.hasUsedFreeTrial === false
      );
    } else {
      // Reset if company data is not available
      setBasicLicenseCount(0);
      setGrowthLicenseCount(0); // Reset Growth
      setProLicenseCount(0);
      setPremiumLicenseCount(0); // Reset Premium
      setHasAnyLicense(false);
    }
  }, [companyData]); // Re-run when companyData updates

  const handleTabClick = (tab) => { setActiveTab(tab); };
  // eslint-disable-next-line no-unused-vars
  const handleOpenAssignModal = () => { setIsAssignModalOpen(true); };
  const handleBuyMoreLicensesClick = () => { navigate("/checkout"); };
  const handleGetLicense = () => { navigate("/checkout"); };
  const handleLearnMore = () => { navigate("/pricing"); };
  const handleCloseAssignModal = () => { setIsAssignModalOpen(false); };
  const handleEditCompany = () => { navigate("/company-edit"); };

  // Keep for modal form inputs
  const handleCreateJobChange = (e) => {
      if (e && e.target) {
          setNewJobData({ ...newJobData, [e.target.name]: e.target.value });
      } else  {
        // Handle react-select change for assessments, ensuring it's always an array
        setNewJobData((prevData) => ({
          ...prevData,
          assessments: Array.isArray(e) ? e : e ? [e] : [], // Correctly handle null/undefined
        }));
      }
  };


  const toggleCreateModal = () => {
    logEvent(analytics, "select_content", { content_type: "ClickCreateJobPosting", content_id: "New" });
    if (hasAnyLicense) {
      setIsCreateModalOpen(!isCreateModalOpen);
    } else {
      setIsLicenseModalOpen(true); // Show license required modal
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen font-sans">
      <Navbar
        onCreateJob={toggleCreateModal}
        userData={userData}
        extraButton={{ label: "Job Offers", path: "/jobpostings", title: "jobOfferLabel" }} // Consider translating "Job Offers"
      />
      <div className="container mx-auto px-4 py-8">
        {/* License Required Modal */}
        {isLicenseModalOpen && (
          <LicenseRequiredModal
            isOpen={isLicenseModalOpen}
            onClose={() => setIsLicenseModalOpen(false)}
            onGetLicense={handleGetLicense}
            onLearnMore={handleLearnMore}
          />
        )}

        {/* Create Job Modal - Props updated to include all license counts */}
        <CreateJobModal
          isOpen={isCreateModalOpen}
          onClose={toggleCreateModal}
          onSubmit={createJob}
          onChange={handleCreateJobChange}
          data={newJobData}
          disableButtons={isCreatingJob}
          assessments={assessments}
          basicLicenseCount={basicLicenseCount}
          growthLicenseCount={growthLicenseCount} // Pass growth count
          proLicenseCount={proLicenseCount}
          premiumLicenseCount={premiumLicenseCount} // Pass premium count
          hasUsedFreeTrial={companyData[0]?.hasUsedFreeTrial ?? true}
        />

        {/* --- License Summary Cards --- */}
        {/* **** Updated Grid for 4 Cards **** */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Basic Licenses Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-slate-600 leading-relaxed mb-2">
              {t("basicLicenses")}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t("licenseCount", { count: basicLicenseCount, type: "basic", })}
            </p>
            <ActionButton onClick={handleBuyMoreLicensesClick} color="lime">
              {t("buyMore")}
            </ActionButton>
          </div>

          {/* **** Growth Licenses Card (NEW) **** */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-slate-600 leading-relaxed mb-2">
              {t("growthLicenses")} {/* Needs translation */}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t("licenseCount", { count: growthLicenseCount, type: "growth", })}
            </p>
            <ActionButton onClick={handleBuyMoreLicensesClick} color="lime">
              {t("buyMore")}
            </ActionButton>
          </div>

          {/* Professional Licenses Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-slate-600 leading-relaxed mb-2">
              {t("proLicenses")} {/* Use 'proLicenses' key for Professional */}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t("licenseCount", { count: proLicenseCount, type: "pro", })}
            </p>
            <ActionButton onClick={handleBuyMoreLicensesClick} color="lime">
              {t("buyMore")}
            </ActionButton>
          </div>

          {/* **** Premium Licenses Card (NEW) **** */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-slate-600 leading-relaxed mb-2">
              {t("premiumLicenses")} {/* Needs translation */}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t("licenseCount", { count: premiumLicenseCount, type: "premium", })}
            </p>
            <ActionButton onClick={handleBuyMoreLicensesClick} color="lime">
              {t("buyMore")}
            </ActionButton>
          </div>
           {/* ********************************* */}

        </div> {/* End License Grid */}

        {/* Edit company button */}
        {userData && userData.canUpdateCompany && (
          <div className="mb-4">
            <ActionButton onClick={handleEditCompany} color="lime">
              {t("editCompany")}
            </ActionButton>
          </div>
        )}

        {/* Tabs Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="flex border-b border-gray-200">
            {/* Tab Buttons */}
            <button
              className={`flex-1 py-3 px-2 text-center text-sm font-medium text-slate-600 leading-relaxed uppercase tracking-wider transition duration-200 ease-in-out ${activeTab === "users" ? "bg-gray-100 text-blue-600 border-b-2 border-blue-600" : "hover:bg-gray-50"}`}
              onClick={() => handleTabClick("users")}
            >
              {t("usersTab")}
            </button>
             <button
               className={`flex-1 py-3 px-2 text-center text-sm font-medium text-slate-600 leading-relaxed uppercase tracking-wider transition duration-200 ease-in-out ${activeTab === "jobOffers" ? "bg-gray-100 text-blue-600 border-b-2 border-blue-600" : "hover:bg-gray-50"}`}
               onClick={() => handleTabClick("jobOffers")}
             >
              {t("jobOffersTab")}
            </button>
            <button
              className={`flex-1 py-3 px-2 text-center text-sm font-medium text-slate-600 leading-relaxed uppercase tracking-wider transition duration-200 ease-in-out ${activeTab === "Assessments" ? "bg-gray-100 text-blue-600 border-b-2 border-blue-600" : "hover:bg-gray-50"}`}
              onClick={() => handleTabClick("Assessments")}
            >
              {t("assessmentsTab")}
            </button>
          </div>

          {/* Tab Content Area */}
          {isFetchingInitialData ? (
            <div className="flex justify-center items-center p-8 min-h-[200px]">
              <ClipLoader color={"#3B82F6"} loading={isFetchingInitialData} size={50} />
            </div>
          ) : (
            <>
              {activeTab === "users" && userData && ( <UsersTable adminInfo={userData} data={usersData} /> )}
              {activeTab === "jobOffers" && userData && ( <JobOffersTable data={jobPostings} tenantId={userData.tenantId} /> )}
              {activeTab === "Assessments" && userData && ( <AssessmentsTable data={assessments} adminInfo={userData} /> )}
            </>
          )}
        </div>

        {/* Assign Job Offers Modal */}
        <AssignJobOfferModal
          isOpen={isAssignModalOpen}
          onClose={handleCloseAssignModal}
          jobOffers={jobPostings}
          users={usersData}
          userData={userData}
        />
      </div>
      <TawkToChat />
    </div>
  );
};

export default DashboardPage;